import { supabase } from '@/shared/supabase/client'
import type { Database } from '@/shared/supabase/database.types'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { screenshotFixtureLocations } from '@/shared/screenshotSeed/synapScreenshotFixtures'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'

export type LocationCategory = 'hospital' | 'consultorio' | 'generico' | 'upa' | 'ubs'

export interface LocationRecord {
  id: string
  name: string
  address: string | null
  category: LocationCategory
  groupId: string | null
  groupName: string | null
  isRemunerated: boolean
  hourlyRateCents: number | null
  estimatedPaymentDay: number | null
  defaultShiftStartTime: string | null
  defaultEstimatedHours: number | null
  /** «Contar atendimentos» neste local (PLT-01/07). */
  countsAttendances: boolean
  createdAt: string
  updatedAt: string
}

export type LocationRemunerationInput = {
  isRemunerated?: boolean
  hourlyRateCents?: number | null
  estimatedPaymentDay?: number | null
  defaultShiftStartTime?: string | null
  defaultEstimatedHours?: number | null
}

const getCurrentUserId = async (): Promise<string> => {
  const { data, error } = await supabase.auth.getUser()
  if (error) throw toRepositoryError(error, 'Não foi possível validar a sessão.')
  if (!data.user) throw toRepositoryError(new Error('Usuário não autenticado.'), 'Usuário não autenticado.', 401)
  return data.user.id
}

export const validateEstimatedPaymentDay = (day: number | null | undefined): void => {
  if (day == null) return
  if (!Number.isInteger(day) || day < 1 || day > 31) {
    throw toRepositoryError(
      new Error('Dia estimado de pagamento deve ser entre 1 e 31.'),
      'Dia estimado de pagamento deve ser entre 1 e 31.',
      422,
    )
  }
}

export const validateHourlyRateCents = (cents: number | null | undefined, isRemunerated: boolean): void => {
  if (!isRemunerated) return
  if (cents == null || !Number.isInteger(cents) || cents < 0) {
    throw toRepositoryError(
      new Error('Informe o valor por hora em centavos quando o local for remunerado.'),
      'Informe o valor por hora em centavos quando o local for remunerado.',
      422,
    )
  }
}

const normalizeRemunerationFields = (
  payload: LocationRemunerationInput,
): Record<string, unknown> => {
  const isRemunerated = payload.isRemunerated ?? false
  validateHourlyRateCents(payload.hourlyRateCents, isRemunerated)
  validateEstimatedPaymentDay(payload.estimatedPaymentDay)

  if (!isRemunerated) {
    return {
      is_remunerated: false,
      hourly_rate_cents: null,
      estimated_payment_day: null,
    }
  }

  return {
    is_remunerated: true,
    hourly_rate_cents: payload.hourlyRateCents ?? null,
    estimated_payment_day: payload.estimatedPaymentDay ?? null,
    ...(payload.defaultShiftStartTime !== undefined
      ? { default_shift_start_time: payload.defaultShiftStartTime || null }
      : {}),
    ...(payload.defaultEstimatedHours !== undefined
      ? { default_estimated_hours: payload.defaultEstimatedHours }
      : {}),
  }
}

const toRecord = (row: {
  id: string
  name: string
  address: string | null
  category: string
  group_id?: string | null
  is_remunerated?: boolean
  hourly_rate_cents?: number | null
  estimated_payment_day?: number | null
  default_shift_start_time?: string | null
  default_estimated_hours?: number | null
  counts_attendances?: boolean
  created_at: string
  updated_at: string
  location_groups?: { id: string; name: string } | { id: string; name: string }[] | null
}): LocationRecord => {
  const groupJoin = Array.isArray(row.location_groups)
    ? row.location_groups[0]
    : row.location_groups
  return {
    id: row.id,
    name: row.name,
    address: row.address ?? null,
    category: row.category as LocationCategory,
    groupId: row.group_id ?? groupJoin?.id ?? null,
    groupName: groupJoin?.name ?? null,
    isRemunerated: row.is_remunerated ?? false,
    hourlyRateCents: row.hourly_rate_cents ?? null,
    estimatedPaymentDay: row.estimated_payment_day ?? null,
    defaultShiftStartTime: row.default_shift_start_time ?? null,
    defaultEstimatedHours: row.default_estimated_hours ?? null,
    countsAttendances: row.counts_attendances ?? false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export const locationsRepository = {
  list: async (): Promise<{ locations: LocationRecord[] }> => {
    if (isScreenshotFixtureMode()) {
      return { locations: screenshotFixtureLocations() }
    }
    const userId = await getCurrentUserId()
    const { data, error } = await supabase
      .from('locations')
      .select('*, location_groups(id, name)')
      .eq('owner_user_id', userId)
      .order('name', { ascending: true })

    if (error) throw toRepositoryError(error, 'Não foi possível listar locais.')
    return { locations: (data ?? []).map(toRecord) }
  },

  create: async (payload: {
    name: string
    address?: string | null
    category?: LocationCategory
    groupId?: string | null
    countsAttendances?: boolean
  } & LocationRemunerationInput): Promise<{ location: LocationRecord }> => {
    const userId = await getCurrentUserId()
    const name = payload.name.trim()
    if (!name) throw toRepositoryError(new Error('Nome é obrigatório.'), 'Nome é obrigatório.', 422)

    const { data, error } = await supabase
      .from('locations')
      .insert({
        owner_user_id: userId,
        name,
        address: payload.address?.trim() || null,
        category: payload.category ?? 'generico',
        group_id: payload.groupId ?? null,
        ...normalizeRemunerationFields(payload),
        default_shift_start_time: payload.defaultShiftStartTime || null,
        default_estimated_hours: payload.defaultEstimatedHours ?? null,
        counts_attendances: payload.countsAttendances ?? false,
      })
      .select('*, location_groups(id, name)')
      .single()

    if (error) throw toRepositoryError(error, 'Não foi possível criar o local.')
    return { location: toRecord(data) }
  },

  update: async (
    id: string,
    payload: {
      name?: string
      address?: string | null
      category?: LocationCategory
      groupId?: string | null
      countsAttendances?: boolean
    } & LocationRemunerationInput,
  ): Promise<{ location: LocationRecord }> => {
    const userId = await getCurrentUserId()
    const updates: Database['public']['Tables']['locations']['Update'] = {}
    if (payload.name !== undefined) {
      const trimmed = payload.name.trim()
      if (!trimmed) throw toRepositoryError(new Error('Nome é obrigatório.'), 'Nome é obrigatório.', 422)
      updates.name = trimmed
    }
    if (payload.address !== undefined) updates.address = payload.address?.trim() || null
    if (payload.category !== undefined) updates.category = payload.category
    if (payload.groupId !== undefined) updates.group_id = payload.groupId
    if (payload.countsAttendances !== undefined) updates.counts_attendances = payload.countsAttendances

    const hasRemunerationToggle = payload.isRemunerated !== undefined
    const hasHourlyRate = payload.hourlyRateCents !== undefined
    const hasPaymentDay = payload.estimatedPaymentDay !== undefined

    if (hasRemunerationToggle && payload.isRemunerated === false) {
      Object.assign(updates, normalizeRemunerationFields({ isRemunerated: false }))
    } else if (hasRemunerationToggle || hasHourlyRate || hasPaymentDay) {
      const isRemunerated = payload.isRemunerated ?? true
      Object.assign(
        updates,
        normalizeRemunerationFields({
          isRemunerated,
          hourlyRateCents: payload.hourlyRateCents,
          estimatedPaymentDay: payload.estimatedPaymentDay,
        }),
      )
    }

    if (payload.defaultShiftStartTime !== undefined) {
      updates.default_shift_start_time = payload.defaultShiftStartTime || null
    }
    if (payload.defaultEstimatedHours !== undefined) {
      updates.default_estimated_hours = payload.defaultEstimatedHours
    }

    const { data, error } = await supabase
      .from('locations')
      .update(updates)
      .eq('id', id)
      .eq('owner_user_id', userId)
      .select('*, location_groups(id, name)')
      .single()

    if (error) throw toRepositoryError(error, 'Não foi possível atualizar o local.')
    return { location: toRecord(data) }
  },

  delete: async (id: string): Promise<void> => {
    const userId = await getCurrentUserId()
    const { error } = await supabase.from('locations').delete().eq('id', id).eq('owner_user_id', userId)
    if (error) throw toRepositoryError(error, 'Não foi possível excluir o local.')
  },
}
